/**
 * CoachPlaybook - TeamSettings Component
 * Version: 0.5.145
 * Description: Team identity and jersey management (opponent scouting moved to Scouting page)
 * 
 * v0.5.121 Changes:
 * - Added logo positioning controls (Y position, size, rotation, flip X/Y)
 * - Controls only show when logo is uploaded
 * - Reset button clears all logo transforms
 * 
 * v0.5.120 Changes:
 * - Now embeds as regular tab instead of modal overlay
 * - onClose is optional (no X button or Cancel when embedded)
 * - Container styling adapts for modal vs embedded mode
 */

import React, { useState, useEffect, useRef } from 'react';
import JerseyIcon, { JERSEY_FONTS, TeamPresets, DEFAULT_JERSEY_TEMPLATE, DEFAULT_POSITIONS, getGoogleFontsLink, BORDER_LIMITS } from '../common/JerseyIcon';
import { getSetting, setSetting } from '../../utils/db';
import { useSport } from '../common';
import { useTheme } from '../../utils/theme';

// Storage keys
const JERSEY_TEMPLATES_KEY = 'jerseyTemplates';
const ACTIVE_TEMPLATE_KEY = 'activeJerseyTemplate';
const TEAM_LOGO_KEY = 'teamLogo';
const TEAM_NAME_KEY = 'teamName';
const CUSTOM_PRESETS_KEY = 'customJerseyPresets';






const TeamSettings = ({ onClose = null }) => {
  const { currentSport, sportConfig } = useSport();
  const { mode, theme } = useTheme();
  const isBasketball = currentSport === 'basketball';

  // Derive THEME from context theme tokens
  const THEME = {
    pageBg: theme.bg,
    panelBg: theme.bgPanel,
    cardBg: theme.bgCard,
    inputBg: theme.bgInput,
    border: theme.border,
    textPrimary: theme.text,
    textSecondary: theme.textSecondary,
    textMuted: theme.textMuted,
    accent: theme.accent,
    accentEnd: mode === 'dark' ? '#38bdf8' : '#60a5fa',
    danger: theme.danger,
    dangerEnd: mode === 'dark' ? '#ef4444' : '#f87171',
    success: theme.success,
    font: "'IBM Plex Sans', sans-serif",
    glass: theme.glass,
    blur: 'blur(12px)',
  };

  const [mainView, setMainView] = useState('myteam');

  // My Team state
  const [teamName, setTeamName] = useState('My Team');
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState('home');
  const [teamLogo, setTeamLogo] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('colors');
  
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  
  // Custom presets
  const [customPresets, setCustomPresets] = useState([]);
  
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef(null);
  
  // Preview state
  const [previewNumber, setPreviewNumber] = useState('23');
  const [previewName, setPreviewName] = useState('SMITH');
  const [previewView, setPreviewView] = useState('back');
  
  // Front/Back independence state
  const [editingView, setEditingView] = useState('both'); // 'front', 'back', or 'both'
  const [linkFrontBack, setLinkFrontBack] = useState(true); // When true, changes apply to both views
  
  // Load saved data
  useEffect(() => {
    const loadData = async () => {
      try {
        const savedTeamName = await getSetting(TEAM_NAME_KEY);
        const savedTemplates = await getSetting(JERSEY_TEMPLATES_KEY);
        const savedActiveId = await getSetting(ACTIVE_TEMPLATE_KEY);
        const savedLogo = await getSetting(TEAM_LOGO_KEY);
        
        if (savedTeamName) setTeamName(savedTeamName);
        
        if (savedTemplates && savedTemplates.length > 0) {
          const migratedTemplates = savedTemplates.map(t => ({
            ...DEFAULT_JERSEY_TEMPLATE,
            ...t,
            numberY: t.numberY ?? DEFAULT_POSITIONS.numberY,
            nameY: t.nameY ?? DEFAULT_POSITIONS.nameY,
            numberSize: t.numberSize ?? DEFAULT_POSITIONS.numberSize,
            nameSize: t.nameSize ?? DEFAULT_POSITIONS.nameSize
          }));
          setTemplates(migratedTemplates);
        } else {
          const defaults = [
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'home', name: 'Home' },
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'away', name: 'Away', fillColor: '#ffffff', outlineColor: '#2563eb', numberColor: '#2563eb', numberOutline: '#1e40af', textColor: '#2563eb' },
            { ...DEFAULT_JERSEY_TEMPLATE, id: 'alternate', name: 'Alternate', fillColor: '#1f2937', outlineColor: '#374151', numberColor: '#f59e0b', numberOutline: '#1f2937', textColor: '#f59e0b' }
          ];
          setTemplates(defaults);
          await setSetting(JERSEY_TEMPLATES_KEY, defaults);
        }
        
        if (savedActiveId) setActiveTemplateId(savedActiveId);
        if (savedLogo) setTeamLogo(savedLogo);
        
        
        // Load custom presets
        const savedPresets = await getSetting(CUSTOM_PRESETS_KEY);
        if (savedPresets) {
          setCustomPresets(savedPresets);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);
  
  // Load Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = getGoogleFontsLink();
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  
  useEffect(() => {
    if (templates.length > 0 && !editingTemplate) {
      const active = templates.find(t => t.id === activeTemplateId) || templates[0];
      // Always merge with defaults to ensure all fields exist
      setEditingTemplate({ 
        ...DEFAULT_JERSEY_TEMPLATE,
        ...active,
        numberY: typeof active.numberY === 'number' ? active.numberY : DEFAULT_POSITIONS.numberY,
        nameY: typeof active.nameY === 'number' ? active.nameY : DEFAULT_POSITIONS.nameY,
        numberSize: typeof active.numberSize === 'number' ? active.numberSize : DEFAULT_POSITIONS.numberSize,
        nameSize: typeof active.nameSize === 'number' ? active.nameSize : DEFAULT_POSITIONS.nameSize
      });
    }
  }, [templates, activeTemplateId]);
  
  // Save functions
  const saveTemplates = async (newTemplates) => {
    setTemplates(newTemplates);
    await setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
  };
  
  
  const saveTeamName = async (name) => {
    setTeamName(name);
    await setSetting(TEAM_NAME_KEY, name);
  };
  
  // Template CRUD
  const handleAddTemplate = async () => {
    if (!newTemplateName.trim()) return;
    const newId = `custom_${Date.now()}`;
    const newTemplate = { ...DEFAULT_JERSEY_TEMPLATE, id: newId, name: newTemplateName.trim().slice(0, 16) };
    const newTemplates = [...templates, newTemplate];
    await saveTemplates(newTemplates);
    setEditingTemplate({ ...newTemplate });
    setActiveTemplateId(newId);
    await setSetting(ACTIVE_TEMPLATE_KEY, newId);
    setNewTemplateName('');
    setShowAddModal(false);
  };
  
  const handleDuplicateTemplate = async () => {
    if (!editingTemplate) return;
    const newId = `copy_${Date.now()}`;
    const duplicated = { ...editingTemplate, id: newId, name: `${editingTemplate.name} Copy`.slice(0, 16) };
    const newTemplates = [...templates, duplicated];
    await saveTemplates(newTemplates);
    setEditingTemplate({ ...duplicated });
    setActiveTemplateId(newId);
    await setSetting(ACTIVE_TEMPLATE_KEY, newId);
  };
  
  const handleRenameTemplate = async () => {
    if (!newTemplateName.trim() || !editingTemplate) return;
    const updated = { ...editingTemplate, name: newTemplateName.trim().slice(0, 16) };
    const newTemplates = templates.map(t => t.id === updated.id ? updated : t);
    await saveTemplates(newTemplates);
    setEditingTemplate(updated);
    setNewTemplateName('');
    setShowRenameModal(false);
  };
  
  const handleRemoveTemplate = async () => {
    if (!templateToDelete || templates.length <= 1) return;
    const newTemplates = templates.filter(t => t.id !== templateToDelete.id);
    await saveTemplates(newTemplates);
    if (editingTemplate?.id === templateToDelete.id) {
      setEditingTemplate({ ...newTemplates[0] });
      setActiveTemplateId(newTemplates[0].id);
      await setSetting(ACTIVE_TEMPLATE_KEY, newTemplates[0].id);
    }
    setShowDeleteConfirm(false);
    setTemplateToDelete(null);
  };
  
  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload an image file'); return; }
    if (file.size > 500 * 1024) { alert('Logo must be under 500KB'); return; }

    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64 = event.target.result;
      setTeamLogo(base64);
      await setSetting(TEAM_LOGO_KEY, base64);
    };
    reader.readAsDataURL(file);
  };
  
  const handleRemoveLogo = async () => {
    setTeamLogo(null);
    await setSetting(TEAM_LOGO_KEY, null);
  };
  
  // Fields that are view-specific (can differ between front and back)
  const VIEW_SPECIFIC_FIELDS = new Set([
    'numberY', 'numberSize', 'numberColor', 'numberOutline', 'numberOutline2', 
    'numberBorderWeight', 'numberBorderWeight2',
    'nameY', 'nameSize', 'textColor', 'textOutline', 'textOutline2',
    'nameBorderWeight', 'nameBorderWeight2',
    'fontFamily'
  ]);
  
  // Map legacy field names to view settings field names
  const FIELD_NAME_MAP = {
    nameY: 'textY',
    nameSize: 'textSize',
    nameBorderWeight: 'textBorderWeight',
    nameBorderWeight2: 'textBorderWeight2'
  };
  
  const updateField = (field, value) => {
    const isViewSpecific = VIEW_SPECIFIC_FIELDS.has(field);
    
    if (isViewSpecific && !linkFrontBack) {
      // When front/back are independent, update the specific view's settings
      const viewKey = editingView === 'both' ? 'front' : editingView;
      const mappedField = FIELD_NAME_MAP[field] || field;
      
      setEditingTemplate(prev => {
        const updatedTemplate = { ...prev };
        
        if (editingView === 'both') {
          // Update both front and back
          updatedTemplate.front = { ...(prev.front || {}), [mappedField]: value };
          updatedTemplate.back = { ...(prev.back || {}), [mappedField]: value };
        } else {
          // Update specific view
          updatedTemplate[viewKey] = { ...(prev[viewKey] || {}), [mappedField]: value };
        }
        
        // Also update legacy field for backward compatibility
        updatedTemplate[field] = value;
        
        return updatedTemplate;
      });
    } else {
      // Linked mode or global field - update at root level (and sync to both views)
      setEditingTemplate(prev => {
        const updatedTemplate = { ...prev, [field]: value };
        
        // If it's a view-specific field and we're in linked mode, sync to both views
        if (isViewSpecific && linkFrontBack) {
          const mappedField = FIELD_NAME_MAP[field] || field;
          updatedTemplate.front = { ...(prev.front || {}), [mappedField]: value };
          updatedTemplate.back = { ...(prev.back || {}), [mappedField]: value };
        }
        
        return updatedTemplate;
      });
    }
  };
  
  // Get the effective value for a field based on current editing view
  const getFieldValue = (field, defaultValue) => {
    if (!editingTemplate) return defaultValue;
    
    const isViewSpecific = VIEW_SPECIFIC_FIELDS.has(field);
    
    if (isViewSpecific && !linkFrontBack && editingView !== 'both') {
      const viewSettings = editingTemplate[editingView];
      const mappedField = FIELD_NAME_MAP[field] || field;
      if (viewSettings && viewSettings[mappedField] !== undefined) {
        return viewSettings[mappedField];
      }
    }
    
    // Fall back to root-level value
    return editingTemplate[field] ?? defaultValue;
  };
  
  // Check if front and back have different fonts (for warning)
  const hasFontMismatch = () => {
    if (!editingTemplate || linkFrontBack) return false;
    const frontFont = editingTemplate.front?.fontFamily || editingTemplate.fontFamily;
    const backFont = editingTemplate.back?.fontFamily || editingTemplate.fontFamily;
    return frontFont && backFont && frontFont !== backFont;
  };
  
  const handleSelectTemplate = (templateId) => {
    if (editingTemplate) {
      const newTemplates = templates.map(t => t.id === editingTemplate.id ? { ...editingTemplate } : t);
      setTemplates(newTemplates);
      setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
    }
    const template = templates.find(t => t.id === templateId);
    if (template) {
      // Always merge with defaults to ensure all fields exist
      setEditingTemplate({ 
        ...DEFAULT_JERSEY_TEMPLATE,
        ...template,
        // Force position values to be numbers (fix for spacing bug)
        numberY: typeof template.numberY === 'number' ? template.numberY : DEFAULT_POSITIONS.numberY,
        nameY: typeof template.nameY === 'number' ? template.nameY : DEFAULT_POSITIONS.nameY,
        numberSize: typeof template.numberSize === 'number' ? template.numberSize : DEFAULT_POSITIONS.numberSize,
        nameSize: typeof template.nameSize === 'number' ? template.nameSize : DEFAULT_POSITIONS.nameSize
      });
      setActiveTemplateId(templateId);
    }
  };
  
  const applyPreset = (presetName) => {
    const preset = TeamPresets[presetName];
    if (preset && editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev, ...preset,
        textColor: preset.numberColor,
        textOutline: preset.numberOutline || preset.outlineColor
      }));
    }
  };
  
  // Custom preset functions
  const applyCustomPreset = (preset) => {
    if (preset && editingTemplate) {
      setEditingTemplate(prev => ({
        ...prev,
        fillColor: preset.fillColor,
        outlineColor: preset.outlineColor,
        numberColor: preset.numberColor,
        numberOutline: preset.numberOutline,
        numberOutline2: preset.numberOutline2,
        textColor: preset.textColor,
        textOutline: preset.textOutline,
        textOutline2: preset.textOutline2,
        fontFamily: preset.fontFamily,
        colorPalette: preset.colorPalette || []
      }));
    }
  };
  
  const saveAsPreset = async () => {
    if (!newPresetName.trim() || !editingTemplate) return;
    const preset = {
      id: `preset_${Date.now()}`,
      name: newPresetName.trim(),
      fillColor: editingTemplate.fillColor,
      outlineColor: editingTemplate.outlineColor,
      numberColor: editingTemplate.numberColor,
      numberOutline: editingTemplate.numberOutline,
      numberOutline2: editingTemplate.numberOutline2,
      textColor: editingTemplate.textColor,
      textOutline: editingTemplate.textOutline,
      textOutline2: editingTemplate.textOutline2,
      fontFamily: editingTemplate.fontFamily,
      colorPalette: editingTemplate.colorPalette || []
    };
    const newPresets = [...customPresets, preset];
    setCustomPresets(newPresets);
    await setSetting(CUSTOM_PRESETS_KEY, newPresets);
    setNewPresetName('');
    setShowSavePresetModal(false);
  };
  
  const deleteCustomPreset = async (presetId) => {
    const newPresets = customPresets.filter(p => p.id !== presetId);
    setCustomPresets(newPresets);
    await setSetting(CUSTOM_PRESETS_KEY, newPresets);
  };
  
  // Color palette functions
  const addToPalette = (color) => {
    if (!editingTemplate) return;
    const currentPalette = editingTemplate.colorPalette || [];
    if (currentPalette.length >= 6) return;
    if (currentPalette.includes(color)) return;
    setEditingTemplate(prev => ({
      ...prev,
      colorPalette: [...currentPalette, color]
    }));
  };
  
  const removeFromPalette = (colorIndex) => {
    if (!editingTemplate) return;
    const currentPalette = editingTemplate.colorPalette || [];
    setEditingTemplate(prev => ({
      ...prev,
      colorPalette: currentPalette.filter((_, i) => i !== colorIndex)
    }));
  };
  
  const applyPaletteColor = (color, field) => {
    if (!editingTemplate) return;
    setEditingTemplate(prev => ({ ...prev, [field]: color }));
  };
  
  // Set default jersey
  const setAsDefaultJersey = async () => {
    if (!editingTemplate) return;
    const newTemplates = templates.map(t => ({
      ...t,
      isDefault: t.id === editingTemplate.id
    }));
    setTemplates(newTemplates);
    await setSetting(JERSEY_TEMPLATES_KEY, newTemplates);
    setEditingTemplate(prev => ({ ...prev, isDefault: true }));
  };
  
  const resetPositioning = () => {
    setEditingTemplate(prev => ({ ...prev, ...DEFAULT_POSITIONS }));
  };
  
  
  
  // Final save
  const handleSave = async () => {
    if (editingTemplate) {
      const newTemplates = templates.map(t => t.id === editingTemplate.id ? { ...editingTemplate } : t);
      await saveTemplates(newTemplates);
      await setSetting(ACTIVE_TEMPLATE_KEY, editingTemplate.id);
    }
    if (onClose) onClose();
  };
  
  if (isLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
        <div style={{ background: THEME.panelBg, borderRadius: '0.5rem', padding: '2rem', border: `1px solid ${THEME.border}` }}>
          <div className="animate-spin" style={{ width: '2rem', height: '2rem', border: `4px solid ${THEME.accent}`, borderTopColor: 'transparent', borderRadius: '9999px', margin: '0 auto' }} />
        </div>
      </div>
    );
  }
  
  // Embedded mode (no onClose) vs Modal mode (with onClose)
  const isModal = !!onClose;
  
  return (
    <div style={isModal
      ? { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '0.5rem' }
      : { height: '100%', display: 'flex', flexDirection: 'column', background: THEME.pageBg, fontFamily: THEME.font }
    }>
      <div style={isModal
        ? { background: THEME.panelBg, borderRadius: '1rem', width: '100%', maxWidth: '56rem', maxHeight: '95vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: `1px solid ${THEME.border}` }
        : { flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }
      }>
        {/* Title Bar */}
        <div style={{ background: `linear-gradient(to right, ${THEME.cardBg}, ${THEME.panelBg})`, padding: '0.75rem 1.5rem', borderBottom: `1px solid ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>⚙️</span>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700, color: THEME.textPrimary, letterSpacing: '0.025em', fontFamily: THEME.font }}>Team Settings</h2>
          </div>
          {onClose && (
            <button onClick={onClose} style={{ padding: '0.5rem', color: THEME.textMuted, borderRadius: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer' }}>
              <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        
        {/* Team Name Section */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.5rem', borderBottom: `1px solid ${THEME.border}` }}>
          <span style={{ fontSize: '1.5rem' }}>👕</span>
          <div>
            <input type="text" value={teamName} onChange={(e) => saveTeamName(e.target.value)}
              style={{ background: 'transparent', fontSize: '1.25rem', fontWeight: 700, color: THEME.textPrimary, borderBottom: '2px solid transparent', outline: 'none', padding: '0.25rem', fontFamily: THEME.font }}
              placeholder="Team Name" />
            <p style={{ fontSize: '0.75rem', color: THEME.textMuted, marginTop: '0.25rem', fontFamily: THEME.font }}>Click to edit team name</p>
          </div>
        </div>
        
        {/* Main View Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${THEME.border}` }}>
          <button onClick={() => setMainView('myteam')}
            style={{ flex: 1, padding: '0.75rem 1.5rem', fontWeight: 600, transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
              background: mainView === 'myteam' ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : 'transparent',
              color: mainView === 'myteam' ? '#fff' : THEME.textMuted }}>
            {isBasketball ? '🏠 My Team Jerseys' : `${sportConfig?.icon || '🏆'} Team Identity`}
          </button>
          <button onClick={() => setMainView('opponents')}
            style={{ flex: 1, padding: '0.75rem 1.5rem', fontWeight: 600, transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
              background: mainView === 'opponents' ? `linear-gradient(135deg, ${THEME.danger}, ${THEME.dangerEnd})` : 'transparent',
              color: mainView === 'opponents' ? '#fff' : THEME.textMuted }}>
            🆚 Opponents
          </button>
        </div>
        
        {/* Content Area */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

          {/* ===== MY TEAM VIEW — Non-Basketball: Simple Team Identity ===== */}
          {mainView === 'myteam' && !isBasketball && (
            <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
              {/* Sport badge */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                <span style={{ fontSize: '32px' }}>{sportConfig?.icon || '🏆'}</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: THEME.textPrimary, fontFamily: THEME.font }}>{sportConfig?.name || 'Sport'} Team Colors</div>
                  <div style={{ fontSize: '12px', color: THEME.textMuted, fontFamily: THEME.font }}>Set your team's primary and secondary colors</div>
                </div>
              </div>

              {/* Team Logo */}
              <div style={{ background: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px', fontFamily: THEME.font }}>Team Logo</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{ width: '80px', height: '80px', borderRadius: '16px', background: THEME.panelBg, border: `2px dashed ${THEME.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {teamLogo ? (
                      <img src={teamLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    ) : (
                      <span style={{ fontSize: '32px', opacity: 0.3 }}>{sportConfig?.icon || '🏆'}</span>
                    )}
                  </div>
                  <div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (ev) => { setTeamLogo(ev.target.result); setSetting('teamLogo', ev.target.result); };
                      reader.readAsDataURL(file);
                    }} style={{ display: 'none' }} />
                    <button onClick={() => fileInputRef.current?.click()}
                      style={{ padding: '8px 16px', background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: THEME.font }}>
                      Upload Logo
                    </button>
                    {teamLogo && (
                      <button onClick={() => { setTeamLogo(null); setSetting('teamLogo', null); }}
                        style={{ marginLeft: '8px', padding: '8px 16px', background: 'transparent', color: THEME.danger, border: `1px solid ${THEME.danger}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: THEME.font }}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Primary Color */}
              <div style={{ background: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: THEME.textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px', fontFamily: THEME.font }}>Team Colors</div>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div>
                    <label style={{ fontSize: '13px', color: THEME.textMuted, display: 'block', marginBottom: '6px', fontFamily: THEME.font }}>Primary Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="color" value={editingTemplate?.fillColor || '#2563eb'}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (editingTemplate) {
                            const updated = { ...editingTemplate, fillColor: color };
                            setEditingTemplate(updated);
                            const newTemplates = templates.map(t => t.id === updated.id ? updated : t);
                            setTemplates(newTemplates);
                            setSetting('jerseyTemplates', newTemplates);
                          }
                        }}
                        style={{ width: '48px', height: '48px', border: `2px solid ${THEME.border}`, borderRadius: '10px', cursor: 'pointer', background: 'transparent', padding: 0 }} />
                      <span style={{ fontSize: '13px', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace" }}>{editingTemplate?.fillColor || '#2563eb'}</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: THEME.textMuted, display: 'block', marginBottom: '6px', fontFamily: THEME.font }}>Secondary Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="color" value={editingTemplate?.outlineColor || '#1e40af'}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (editingTemplate) {
                            const updated = { ...editingTemplate, outlineColor: color };
                            setEditingTemplate(updated);
                            const newTemplates = templates.map(t => t.id === updated.id ? updated : t);
                            setTemplates(newTemplates);
                            setSetting('jerseyTemplates', newTemplates);
                          }
                        }}
                        style={{ width: '48px', height: '48px', border: `2px solid ${THEME.border}`, borderRadius: '10px', cursor: 'pointer', background: 'transparent', padding: 0 }} />
                      <span style={{ fontSize: '13px', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace" }}>{editingTemplate?.outlineColor || '#1e40af'}</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', color: THEME.textMuted, display: 'block', marginBottom: '6px', fontFamily: THEME.font }}>Accent Color</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input type="color" value={editingTemplate?.numberColor || '#ffffff'}
                        onChange={(e) => {
                          const color = e.target.value;
                          if (editingTemplate) {
                            const updated = { ...editingTemplate, numberColor: color };
                            setEditingTemplate(updated);
                            const newTemplates = templates.map(t => t.id === updated.id ? updated : t);
                            setTemplates(newTemplates);
                            setSetting('jerseyTemplates', newTemplates);
                          }
                        }}
                        style={{ width: '48px', height: '48px', border: `2px solid ${THEME.border}`, borderRadius: '10px', cursor: 'pointer', background: 'transparent', padding: 0 }} />
                      <span style={{ fontSize: '13px', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace" }}>{editingTemplate?.numberColor || '#ffffff'}</span>
                    </div>
                  </div>
                </div>

                {/* Color preview */}
                <div style={{ marginTop: '20px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <div style={{ fontSize: '12px', color: THEME.textMuted, fontFamily: THEME.font }}>Preview:</div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{ width: '40px', height: '24px', borderRadius: '4px', background: editingTemplate?.fillColor || '#2563eb' }} />
                    <div style={{ width: '40px', height: '24px', borderRadius: '4px', background: editingTemplate?.outlineColor || '#1e40af' }} />
                    <div style={{ width: '40px', height: '24px', borderRadius: '4px', background: editingTemplate?.numberColor || '#ffffff', border: `1px solid ${THEME.border}` }} />
                  </div>
                </div>
              </div>

              {/* Info note */}
              <div style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '10px', padding: '14px 16px', fontSize: '13px', color: THEME.textSecondary, fontFamily: THEME.font, lineHeight: 1.5 }}>
                These colors will be applied to your team's player markers on the playing surface. Opponent team colors can be set in the Scouting page.
              </div>
            </div>
          )}

          {/* ===== MY TEAM VIEW — Basketball: Full Jersey Designer ===== */}
          {mainView === 'myteam' && isBasketball && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Template Selector - Fixed at top */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', padding: '1.5rem 1.5rem 1rem', background: THEME.pageBg, borderBottom: `1px solid ${THEME.border}` }}>
                {templates.map(template => (
                  <button key={template.id} onClick={() => handleSelectTemplate(template.id)}
                    style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 500, transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                      background: editingTemplate?.id === template.id ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                      color: editingTemplate?.id === template.id ? '#fff' : THEME.textSecondary,
                      boxShadow: editingTemplate?.id === template.id ? `0 4px 12px rgba(14,165,233,0.3)` : 'none' }}>
                    <div style={{ width: '0.75rem', height: '0.75rem', borderRadius: '9999px', display: 'inline-block', marginRight: '0.5rem', backgroundColor: template.fillColor }} />
                    {template.name}
                  </button>
                ))}
                <button onClick={() => setShowAddModal(true)} style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 500, background: `linear-gradient(135deg, ${THEME.success}, #34d399)`, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>+ New</button>
              </div>
              
              {/* Two-column layout with independent scrolling */}
              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', overflow: 'hidden' }}>
                {/* Left: Preview - Sticky/Fixed */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', background: THEME.pageBg, borderRight: `1px solid ${THEME.border}` }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: 0 }}>
                  <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1.5rem', border: `1px solid ${THEME.border}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                      <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: THEME.textPrimary, fontFamily: THEME.font }}>Preview</h3>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {['front', 'back'].map(v => (
                          <button key={v} onClick={() => setPreviewView(v)}
                            style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', borderRadius: '0.5rem', transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                              background: previewView === v ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                              color: previewView === v ? '#fff' : THEME.textSecondary }}>
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                          </button>
                        ))}
                    </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '1rem', background: THEME.pageBg, borderRadius: '0.5rem' }}>
                      <JerseyIcon size={180} number={previewNumber} view={previewView}
                        teamName={previewView === 'front' ? teamName.toUpperCase() : ''}
                        lastName={previewView === 'back' ? previewName : ''}
                        fillColor={editingTemplate?.fillColor} outlineColor={editingTemplate?.outlineColor}
                        numberColor={editingTemplate?.numberColor} numberOutline={editingTemplate?.numberOutline}
                        numberOutline2={editingTemplate?.numberOutline2}
                        textColor={editingTemplate?.textColor} textOutline={editingTemplate?.textOutline}
                        textOutline2={editingTemplate?.textOutline2}
                        fontFamily={editingTemplate?.fontFamily} logoUrl={previewView === 'front' ? teamLogo : null}
                        logoY={editingTemplate?.logoY ?? 65}
                        logoSize={editingTemplate?.logoSize ?? 50}
                        logoRotation={editingTemplate?.logoRotation ?? 0}
                        logoFlipX={editingTemplate?.logoFlipX ?? false}
                        logoFlipY={editingTemplate?.logoFlipY ?? false}
                        numberY={editingTemplate?.numberY ?? DEFAULT_POSITIONS.numberY} 
                        nameY={editingTemplate?.nameY ?? DEFAULT_POSITIONS.nameY}
                        numberSize={editingTemplate?.numberSize ?? DEFAULT_POSITIONS.numberSize} 
                        nameSize={editingTemplate?.nameSize ?? DEFAULT_POSITIONS.nameSize}
                        jerseyBorderWeight={editingTemplate?.jerseyBorderWeight ?? DEFAULT_POSITIONS.jerseyBorderWeight}
                        numberBorderWeight={editingTemplate?.numberBorderWeight ?? DEFAULT_POSITIONS.numberBorderWeight}
                        numberBorderWeight2={editingTemplate?.numberBorderWeight2 ?? 0}
                        nameBorderWeight={editingTemplate?.nameBorderWeight ?? DEFAULT_POSITIONS.nameBorderWeight}
                        nameBorderWeight2={editingTemplate?.nameBorderWeight2 ?? 0}
                        front={editingTemplate?.front}
                        back={editingTemplate?.back} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: THEME.textMuted, marginBottom: '0.25rem', fontFamily: THEME.font }}>Number</label>
                        <input type="text" value={previewNumber} onChange={(e) => setPreviewNumber(e.target.value.slice(0, 3))} maxLength={3}
                          style={{ width: '100%', padding: '0.5rem 0.75rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textPrimary, textAlign: 'center', fontFamily: THEME.font, outline: 'none' }} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', color: THEME.textMuted, marginBottom: '0.25rem', fontFamily: THEME.font }}>Name</label>
                        <input type="text" value={previewName} onChange={(e) => setPreviewName(e.target.value.toUpperCase().slice(0, 12))} maxLength={12}
                          style={{ width: '100%', padding: '0.5rem 0.75rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textPrimary, textAlign: 'center', fontFamily: THEME.font, outline: 'none' }} />
                      </div>
                    </div>
                  </div>
                  
                  {/* Template Actions */}
                  <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '0.75rem', fontFamily: THEME.font }}>Template Actions</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
                      <button onClick={() => { setNewTemplateName(editingTemplate?.name || ''); setShowRenameModal(true); }}
                        style={{ padding: '0.5rem 0.75rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', fontSize: '0.875rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>✏️ Rename</button>
                      <button onClick={handleDuplicateTemplate}
                        style={{ padding: '0.5rem 0.75rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', fontSize: '0.875rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>📋 Duplicate</button>
                      <button onClick={() => { setTemplateToDelete(editingTemplate); setShowDeleteConfirm(true); }}
                        disabled={templates.length <= 1}
                        style={{ padding: '0.5rem 0.75rem', background: 'rgba(220,38,38,0.2)', color: '#f87171', borderRadius: '0.5rem', fontSize: '0.875rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font, opacity: templates.length <= 1 ? 0.5 : 1 }}>🗑️ Delete</button>
                    </div>
                  </div>
                  
                  {/* Logo Upload */}
                  <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '0.75rem', fontFamily: THEME.font }}>Team Logo</h3>
                    {teamLogo ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <img src={teamLogo} alt="Logo" style={{ width: '4rem', height: '4rem', objectFit: 'contain', background: '#fff', borderRadius: '0.5rem', padding: '0.25rem' }}/>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <button onClick={() => fileInputRef.current?.click()} style={{ padding: '0.5rem 1rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', fontSize: '0.875rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Change</button>
                            <button onClick={handleRemoveLogo} style={{ padding: '0.5rem 1rem', background: 'rgba(220,38,38,0.2)', color: '#f87171', borderRadius: '0.5rem', fontSize: '0.875rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Remove</button>
                          </div>
                        </div>

                        {/* Logo Controls */}
                        <div style={{ borderTop: `1px solid ${THEME.border}`, paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          <p style={{ fontSize: '0.75rem', color: THEME.textMuted, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: THEME.font }}>Logo Controls</p>
                          
                          {/* Position Y */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '4rem', fontFamily: THEME.font }}>Position</span>
                            <input type="range" min="10" max="90"
                              value={editingTemplate?.logoY ?? 65}
                              onChange={(e) => updateField('logoY', parseInt(e.target.value))}
                              style={{ flex: 1, accentColor: THEME.accent, cursor: 'pointer' }} />
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '2rem', textAlign: 'right', fontFamily: THEME.font }}>{editingTemplate?.logoY ?? 65}%</span>
                          </div>

                          {/* Size */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '4rem', fontFamily: THEME.font }}>Size</span>
                            <input type="range" min="10" max="100"
                              value={editingTemplate?.logoSize ?? 50}
                              onChange={(e) => updateField('logoSize', parseInt(e.target.value))}
                              style={{ flex: 1, accentColor: THEME.accent, cursor: 'pointer' }} />
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '2rem', textAlign: 'right', fontFamily: THEME.font }}>{editingTemplate?.logoSize ?? 50}%</span>
                          </div>

                          {/* Rotation */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '4rem', fontFamily: THEME.font }}>Rotate</span>
                            <input type="range" min="-180" max="180"
                              value={editingTemplate?.logoRotation ?? 0}
                              onChange={(e) => updateField('logoRotation', parseInt(e.target.value))}
                              style={{ flex: 1, accentColor: THEME.accent, cursor: 'pointer' }} />
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '2rem', textAlign: 'right', fontFamily: THEME.font }}>{editingTemplate?.logoRotation ?? 0}°</span>
                          </div>

                          {/* Flip buttons */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, width: '4rem', fontFamily: THEME.font }}>Flip</span>
                            <button onClick={() => updateField('logoFlipX', !(editingTemplate?.logoFlipX))}
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', borderRadius: '0.5rem', transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                                background: editingTemplate?.logoFlipX ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                                color: editingTemplate?.logoFlipX ? '#fff' : THEME.textMuted }}>
                              ↔️ Horizontal
                            </button>
                            <button onClick={() => updateField('logoFlipY', !(editingTemplate?.logoFlipY))}
                              style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', borderRadius: '0.5rem', transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                                background: editingTemplate?.logoFlipY ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                                color: editingTemplate?.logoFlipY ? '#fff' : THEME.textMuted }}>
                              ↕️ Vertical
                            </button>
                            {(editingTemplate?.logoRotation !== 0 || editingTemplate?.logoFlipX || editingTemplate?.logoFlipY) && (
                              <button onClick={() => { updateField('logoRotation', 0); updateField('logoFlipX', false); updateField('logoFlipY', false); }}
                                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', background: THEME.cardBg, color: '#fb923c', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>
                                ↺ Reset
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => fileInputRef.current?.click()}
                        style={{ width: '100%', padding: '1.5rem', border: `2px dashed ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textMuted, background: 'transparent', cursor: 'pointer', fontFamily: THEME.font }}>
                        📤 Upload Logo (max 500KB)
                      </button>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleLogoUpload(e, false)} style={{ display: 'none' }}/>
                  </div>
                  </div>
                </div>
                
                {/* Right: Controls - Scrollable */}
                <div style={{ padding: '1.5rem', overflowY: 'auto', background: THEME.pageBg }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'flex', gap: '0.25rem', background: THEME.panelBg, padding: '0.25rem', borderRadius: '0.75rem', border: `1px solid ${THEME.border}` }}>
                    {[{ id: 'colors', label: '🎨 Colors' }, { id: 'positioning', label: '📐 Layout' }, { id: 'presets', label: '⚡ Presets' }].map(tab => (
                      <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        style={{ flex: 1, padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 500, transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                          background: activeTab === tab.id ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : 'transparent',
                          color: activeTab === tab.id ? '#fff' : THEME.textMuted }}>
                        {tab.label}
                      </button>
                    ))}
                  </div>
                  
                  {activeTab === 'colors' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Front/Back toggle for colors too */}
                      {!linkFrontBack && (
                        <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '0.75rem', border: `1px solid ${THEME.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                            <span style={{ fontSize: '0.75rem', color: THEME.textMuted, fontFamily: THEME.font }}>Editing colors for:</span>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {['front', 'back', 'both'].map(v => (
                                <button
                                  key={v}
                                  onClick={() => {
                                    setEditingView(v);
                                    if (v !== 'both') setPreviewView(v);
                                  }}
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', borderRadius: '0.25rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                                    background: editingView === v ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                                    color: editingView === v ? '#fff' : THEME.textMuted }}
                                >
                                  {v === 'both' ? 'Both' : v.charAt(0).toUpperCase() + v.slice(1)}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Jersey Base Colors - Always global */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>Jersey Colors <span style={{ fontSize: '0.75rem', color: THEME.textMuted }}>(applies to both)</span></h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {[{ key: 'fillColor', label: 'Body Color' }, { key: 'outlineColor', label: 'Outline/Trim' }].map(({ key, label }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <input type="color" value={editingTemplate?.[key] || '#000000'} onChange={(e) => updateField(key, e.target.value)}
                                style={{ width: '3rem', height: '3rem', borderRadius: '0.5rem', cursor: 'pointer', border: `2px solid ${THEME.border}` }} />
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', color: THEME.textPrimary, marginBottom: '0.25rem', fontFamily: THEME.font }}>{label}</label>
                                <input type="text" value={editingTemplate?.[key] || ''} onChange={(e) => updateField(key, e.target.value)}
                                  style={{ width: '100%', padding: '0.375rem 0.75rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.25rem', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.875rem', outline: 'none' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Number & Text Colors - View-specific when unlinked */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>
                          Number Colors
                          {!linkFrontBack && editingView !== 'both' && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: THEME.accent }}>({editingView} only)</span>
                          )}
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {[{ key: 'numberColor', label: 'Number Fill' }, { key: 'numberOutline', label: 'Number Stroke' }].map(({ key, label }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <input type="color" value={getFieldValue(key, '#000000')} onChange={(e) => updateField(key, e.target.value)}
                                style={{ width: '3rem', height: '3rem', borderRadius: '0.5rem', cursor: 'pointer', border: `2px solid ${THEME.border}` }} />
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', color: THEME.textPrimary, marginBottom: '0.25rem', fontFamily: THEME.font }}>{label}</label>
                                <input type="text" value={getFieldValue(key, '')} onChange={(e) => updateField(key, e.target.value)}
                                  style={{ width: '100%', padding: '0.375rem 0.75rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.25rem', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.875rem', outline: 'none' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>
                          Name Text Colors
                          {!linkFrontBack && editingView !== 'both' && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: THEME.accent }}>({editingView} only)</span>
                          )}
                        </h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                          {[{ key: 'textColor', label: 'Text Fill' }, { key: 'textOutline', label: 'Text Stroke' }].map(({ key, label }) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <input type="color" value={getFieldValue(key, '#000000')} onChange={(e) => updateField(key, e.target.value)}
                                style={{ width: '3rem', height: '3rem', borderRadius: '0.5rem', cursor: 'pointer', border: `2px solid ${THEME.border}` }} />
                              <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', color: THEME.textPrimary, marginBottom: '0.25rem', fontFamily: THEME.font }}>{label}</label>
                                <input type="text" value={getFieldValue(key, '')} onChange={(e) => updateField(key, e.target.value)}
                                  style={{ width: '100%', padding: '0.375rem 0.75rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.25rem', color: THEME.textSecondary, fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.875rem', outline: 'none' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '0.75rem', fontFamily: THEME.font }}>
                          Font Style
                          {!linkFrontBack && editingView !== 'both' && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: THEME.accent }}>({editingView} only)</span>
                          )}
                        </h3>
                        {/* Group fonts by category */}
                        {['Athletic', 'Block', 'Classic', 'Collegiate', 'Street', 'Marker', 'Script'].map(category => {
                          const fontsInCategory = JERSEY_FONTS.filter(f => f.category === category);
                          if (fontsInCategory.length === 0) return null;
                          return (
                            <div key={category} style={{ marginBottom: '1rem' }}>
                              <div style={{ fontSize: '0.75rem', color: THEME.textMuted, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: THEME.font }}>
                                {category === 'Street' ? '🏙️ Street/Graffiti' :
                                 category === 'Marker' ? '✏️ Marker/Whiteboard' :
                                 category === 'Script' ? '🖌️ Script/Brush' : category}
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                {fontsInCategory.map(font => (
                                  <button key={font.id} onClick={() => updateField('fontFamily', font.family)}
                                    style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', fontWeight: 500, fontSize: '0.875rem', transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: font.family,
                                      background: getFieldValue('fontFamily', '') === font.family ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : THEME.cardBg,
                                      color: getFieldValue('fontFamily', '') === font.family ? '#fff' : THEME.textSecondary }}>
                                    {font.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {/* Font mismatch warning in colors tab too */}
                        {hasFontMismatch() && (
                          <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'rgba(202,138,4,0.2)', border: '1px solid rgba(234,179,8,0.5)', borderRadius: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#facc15', fontSize: '0.75rem', fontFamily: THEME.font }}>
                              <span>⚠️</span>
                              <span>Front and back use different fonts</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'positioning' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Front/Back Independence Control */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textPrimary, fontFamily: THEME.font }}>🎽 Front/Back Settings</h3>
                          <button
                            onClick={() => {
                              setLinkFrontBack(!linkFrontBack);
                              if (!linkFrontBack) setEditingView('both');
                            }}
                            style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem', borderRadius: '0.5rem', fontWeight: 500, transition: 'all 0.2s', cursor: 'pointer', fontFamily: THEME.font,
                              background: linkFrontBack ? 'rgba(22,163,74,0.3)' : 'rgba(147,51,234,0.3)',
                              color: linkFrontBack ? '#4ade80' : '#c084fc',
                              border: linkFrontBack ? '1px solid rgba(34,197,94,0.5)' : '1px solid rgba(168,85,247,0.5)' }}>
                            {linkFrontBack ? '🔗 Linked' : '🔓 Independent'}
                          </button>
                        </div>

                        {!linkFrontBack && (
                          <div style={{ display: 'flex', gap: '0.25rem', background: 'rgba(10,14,23,0.5)', padding: '0.25rem', borderRadius: '0.5rem' }}>
                            {[
                              { id: 'front', label: '👕 Front' },
                              { id: 'back', label: '🔢 Back' },
                              { id: 'both', label: '⚡ Both' }
                            ].map(view => (
                              <button
                                key={view.id}
                                onClick={() => {
                                  setEditingView(view.id);
                                  setPreviewView(view.id === 'both' ? 'back' : view.id);
                                }}
                                style={{ flex: 1, padding: '0.5rem 0.75rem', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 500, transition: 'all 0.2s', border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                                  background: editingView === view.id ? `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})` : 'transparent',
                                  color: editingView === view.id ? '#fff' : THEME.textMuted }}>
                                {view.label}
                              </button>
                            ))}
                          </div>
                        )}

                        {!linkFrontBack && editingView !== 'both' && (
                          <p style={{ fontSize: '0.75rem', color: THEME.textMuted, marginTop: '0.5rem', fontFamily: THEME.font }}>
                            Editing <span style={{ color: THEME.accent, fontWeight: 500 }}>{editingView === 'front' ? 'Front' : 'Back'}</span> view only
                          </p>
                        )}

                        {/* Font mismatch warning */}
                        {hasFontMismatch() && (
                          <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'rgba(202,138,4,0.2)', border: '1px solid rgba(234,179,8,0.5)', borderRadius: '0.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#facc15', fontSize: '0.75rem', fontFamily: THEME.font }}>
                              <span>⚠️</span>
                              <span>Front and back use different fonts - this may look inconsistent</span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, fontFamily: THEME.font }}>Number Position & Size</h3>
                          <button onClick={resetPositioning} style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem', background: THEME.cardBg, color: THEME.textMuted, borderRadius: '0.25rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Reset</button>
                        </div>
                        {[{ key: 'numberY', label: 'Vertical Position', min: 20, max: 80 }, { key: 'numberSize', label: 'Number Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                          <div key={key} style={{ marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>{label}</label>
                              <span style={{ fontSize: '0.875rem', color: THEME.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue(key, 50)}%</span>
                            </div>
                            <input type="range" min={min} max={max} value={getFieldValue(key, 50)} onChange={(e) => updateField(key, parseInt(e.target.value))}
                              style={{ width: '100%', accentColor: THEME.accent, cursor: 'pointer' }} />
                          </div>
                        ))}
                      </div>
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>Name Position & Size</h3>
                        {[{ key: 'nameY', label: 'Vertical Position', min: 0, max: 50 }, { key: 'nameSize', label: 'Name Size', min: 50, max: 150 }].map(({ key, label, min, max }) => (
                          <div key={key} style={{ marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                              <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>{label}</label>
                              <span style={{ fontSize: '0.875rem', color: THEME.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue(key, 50)}%</span>
                            </div>
                            <input type="range" min={min} max={max} value={getFieldValue(key, 50)} onChange={(e) => updateField(key, parseInt(e.target.value))}
                              style={{ width: '100%', accentColor: THEME.accent, cursor: 'pointer' }} />
                          </div>
                        ))}
                      </div>
                      
                      {/* Border Weight Controls */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>Jersey Border</h3>
                        <div style={{ marginBottom: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>Jersey Outline</label>
                            <span style={{ fontSize: '0.875rem', color: '#fb923c', fontFamily: "'IBM Plex Mono', monospace" }}>{editingTemplate?.jerseyBorderWeight ?? 12}px</span>
                          </div>
                          <input type="range" min={BORDER_LIMITS.jersey.min} max={BORDER_LIMITS.jersey.max}
                            value={editingTemplate?.jerseyBorderWeight ?? 12}
                            onChange={(e) => updateField('jerseyBorderWeight', parseInt(e.target.value))}
                            style={{ width: '100%', accentColor: '#f97316', cursor: 'pointer' }} />
                        </div>
                      </div>
                      
                      {/* Number Borders - Inner + Outer */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>Number Borders</h3>

                        {/* Inner Border */}
                        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: `1px solid ${THEME.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <input type="color" value={getFieldValue('numberOutline', '#1e40af')}
                              onChange={(e) => updateField('numberOutline', e.target.value)}
                              style={{ width: '2rem', height: '2rem', borderRadius: '0.25rem', cursor: 'pointer', border: `1px solid ${THEME.border}` }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>Inner Border</label>
                                <span style={{ fontSize: '0.875rem', color: THEME.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue('numberBorderWeight', 6)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.numberInner.min} max={BORDER_LIMITS.numberInner.max}
                            value={getFieldValue('numberBorderWeight', 6)}
                            onChange={(e) => updateField('numberBorderWeight', parseInt(e.target.value))}
                            style={{ width: '100%', accentColor: THEME.accent, cursor: 'pointer' }} />
                        </div>

                        {/* Outer Border (Double Border) */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <input type="color" value={getFieldValue('numberOutline2', '#000000')}
                              onChange={(e) => updateField('numberOutline2', e.target.value)}
                              style={{ width: '2rem', height: '2rem', borderRadius: '0.25rem', cursor: 'pointer', border: `1px solid ${THEME.border}` }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>Outer Border <span style={{ color: THEME.textMuted }}>(double)</span></label>
                                <span style={{ fontSize: '0.875rem', color: '#c084fc', fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue('numberBorderWeight2', 0)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.numberOuter.min} max={BORDER_LIMITS.numberOuter.max}
                            value={getFieldValue('numberBorderWeight2', 0)}
                            onChange={(e) => updateField('numberBorderWeight2', parseInt(e.target.value))}
                            style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer' }} />
                          <p style={{ fontSize: '0.75rem', color: THEME.textMuted, marginTop: '0.25rem', fontFamily: THEME.font }}>Set to 0 to disable outer border</p>
                        </div>
                      </div>
                      
                      {/* Name Borders - Inner + Outer */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>Name Borders</h3>

                        {/* Inner Border */}
                        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: `1px solid ${THEME.border}` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <input type="color" value={getFieldValue('textOutline', '#1e40af')}
                              onChange={(e) => updateField('textOutline', e.target.value)}
                              style={{ width: '2rem', height: '2rem', borderRadius: '0.25rem', cursor: 'pointer', border: `1px solid ${THEME.border}` }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>Inner Border</label>
                                <span style={{ fontSize: '0.875rem', color: THEME.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue('nameBorderWeight', 4)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.nameInner.min} max={BORDER_LIMITS.nameInner.max}
                            value={getFieldValue('nameBorderWeight', 4)}
                            onChange={(e) => updateField('nameBorderWeight', parseInt(e.target.value))}
                            style={{ width: '100%', accentColor: THEME.accent, cursor: 'pointer' }} />
                        </div>

                        {/* Outer Border (Double Border) */}
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                            <input type="color" value={getFieldValue('textOutline2', '#000000')}
                              onChange={(e) => updateField('textOutline2', e.target.value)}
                              style={{ width: '2rem', height: '2rem', borderRadius: '0.25rem', cursor: 'pointer', border: `1px solid ${THEME.border}` }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <label style={{ fontSize: '0.875rem', color: THEME.textPrimary, fontFamily: THEME.font }}>Outer Border <span style={{ color: THEME.textMuted }}>(double)</span></label>
                                <span style={{ fontSize: '0.875rem', color: '#c084fc', fontFamily: "'IBM Plex Mono', monospace" }}>{getFieldValue('nameBorderWeight2', 0)}px</span>
                              </div>
                            </div>
                          </div>
                          <input type="range" min={BORDER_LIMITS.nameOuter.min} max={BORDER_LIMITS.nameOuter.max}
                            value={getFieldValue('nameBorderWeight2', 0)}
                            onChange={(e) => updateField('nameBorderWeight2', parseInt(e.target.value))}
                            style={{ width: '100%', accentColor: '#a855f7', cursor: 'pointer' }} />
                          <p style={{ fontSize: '0.75rem', color: THEME.textMuted, marginTop: '0.25rem', fontFamily: THEME.font }}>Set to 0 to disable outer border</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'presets' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {/* Color Palette */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, fontFamily: THEME.font }}>Team Color Palette</h3>
                          <span style={{ fontSize: '0.75rem', color: THEME.textMuted, fontFamily: THEME.font }}>{(editingTemplate?.colorPalette || []).length}/6</span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                          {(editingTemplate?.colorPalette || []).map((color, i) => (
                            <div key={i} style={{ position: 'relative' }}>
                              <button
                                style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.5rem', border: `2px solid ${THEME.border}`, backgroundColor: color, cursor: 'pointer' }}
                                onClick={() => {}}
                                title={color}
                              />
                              <button
                                onClick={() => removeFromPalette(i)}
                                style={{ position: 'absolute', top: '-0.25rem', right: '-0.25rem', width: '1rem', height: '1rem', background: THEME.danger, borderRadius: '9999px', color: '#fff', fontSize: '0.75rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >×</button>
                            </div>
                          ))}
                          {(editingTemplate?.colorPalette || []).length < 6 && (
                            <button
                              onClick={() => addToPalette(editingTemplate?.fillColor)}
                              style={{ width: '2.5rem', height: '2.5rem', borderRadius: '0.5rem', border: `2px dashed ${THEME.border}`, color: THEME.textMuted, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}
                            >+</button>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {[{ label: '+ Body', color: 'fillColor' }, { label: '+ Outline', color: 'outlineColor' }, { label: '+ Number', color: 'numberColor' }, { label: '+ Text', color: 'textColor' }].map(item => (
                            <button key={item.color} onClick={() => addToPalette(editingTemplate?.[item.color])} style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.25rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>{item.label}</button>
                          ))}
                        </div>
                        {(editingTemplate?.colorPalette || []).length > 0 && (
                          <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: `1px solid ${THEME.border}` }}>
                            <p style={{ fontSize: '0.75rem', color: THEME.textMuted, marginBottom: '0.5rem', fontFamily: THEME.font }}>Apply palette color to:</p>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {['fillColor', 'outlineColor', 'numberColor', 'numberOutline', 'textColor', 'textOutline'].map(field => (
                                <select key={field} onChange={(e) => e.target.value && applyPaletteColor(e.target.value, field)} value=""
                                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem', background: THEME.inputBg, color: THEME.textSecondary, borderRadius: '0.25rem', border: `1px solid ${THEME.border}`, fontFamily: THEME.font, outline: 'none' }}>
                                  <option value="">{field.replace('Color', '').replace('Outline', ' Outline')}</option>
                                  {(editingTemplate?.colorPalette || []).map((c, i) => (
                                    <option key={i} value={c}>{c}</option>
                                  ))}
                                </select>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Save as Preset & Default Jersey */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '0.75rem', fontFamily: THEME.font }}>Quick Actions</h3>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button onClick={() => setShowSavePresetModal(true)}
                            style={{ padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${THEME.success}, #34d399)`, color: '#fff', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>
                            💾 Save as Preset
                          </button>
                          <button onClick={setAsDefaultJersey}
                            style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: THEME.font,
                              background: editingTemplate?.isDefault ? '#ca8a04' : THEME.cardBg,
                              color: editingTemplate?.isDefault ? '#fff' : THEME.textSecondary }}>
                            {editingTemplate?.isDefault ? '⭐ Default Jersey' : '☆ Set as Default'}
                          </button>
                        </div>
                        {editingTemplate?.isDefault && (
                          <p style={{ fontSize: '0.75rem', color: '#facc15', marginTop: '0.5rem', fontFamily: THEME.font }}>This jersey is used as the display default</p>
                        )}
                      </div>

                      {/* Custom Presets */}
                      {customPresets.length > 0 && (
                        <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>My Saved Presets</h3>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                            {customPresets.map(preset => (
                              <div key={preset.id} style={{ position: 'relative' }}>
                                <button onClick={() => applyCustomPreset(preset)}
                                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: THEME.cardBg, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer' }}>
                                  <div style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', border: '2px solid', backgroundColor: preset.fillColor, borderColor: preset.outlineColor }} />
                                  <span style={{ color: THEME.textPrimary, fontWeight: 500, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: THEME.font }}>{preset.name}</span>
                                </button>
                                <button onClick={() => deleteCustomPreset(preset.id)}
                                  style={{ position: 'absolute', top: '-0.5rem', right: '-0.5rem', width: '1.5rem', height: '1.5rem', background: THEME.danger, borderRadius: '9999px', color: '#fff', fontSize: '0.75rem', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* NBA Team Presets */}
                      <div style={{ background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${THEME.border}` }}>
                        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: THEME.textMuted, marginBottom: '1rem', fontFamily: THEME.font }}>NBA Team Presets</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                          {Object.entries(TeamPresets).map(([name, colors]) => (
                            <button key={name} onClick={() => applyPreset(name)}
                              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: THEME.cardBg, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer' }}>
                              <div style={{ width: '2rem', height: '2rem', borderRadius: '0.5rem', border: '2px solid', backgroundColor: colors.fillColor, borderColor: colors.outlineColor }} />
                              <span style={{ color: THEME.textPrimary, fontWeight: 500, fontFamily: THEME.font }}>{name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* ===== OPPONENTS VIEW — Redirect to Scouting ===== */}
          {mainView === 'opponents' && (
            <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center', padding: '3rem', background: THEME.glass, backdropFilter: THEME.blur, borderRadius: '0.75rem', border: `1px solid ${THEME.border}`, maxWidth: '400px' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔍</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: THEME.textPrimary, marginBottom: '0.75rem', fontFamily: THEME.font }}>Opponent Scouting</h3>
                <p style={{ color: THEME.textSecondary, marginBottom: '1.5rem', lineHeight: 1.6, fontFamily: THEME.font }}>
                  Opponent teams and player scouting have moved to the dedicated Scouting page for a better experience.
                </p>
                <div style={{ padding: '0.75rem 1.5rem', background: `linear-gradient(135deg, ${THEME.danger}, ${THEME.dangerEnd})`, color: '#fff', borderRadius: '0.5rem', fontWeight: 600, fontFamily: THEME.font, display: 'inline-block' }}>
                  Go to Scouting page
                </div>
                <p style={{ color: THEME.textMuted, fontSize: '0.75rem', marginTop: '1rem', fontFamily: THEME.font }}>
                  Stats &gt; Scouting in the sidebar
                </p>
              </div>
            </div>
          )}

        </div>

        {/* Opponent management moved to Scouting page */}
        {/* Footer */}
        {mainView === 'myteam' && (
          <div style={{ padding: '1rem 1.5rem', borderTop: `1px solid ${THEME.border}`, display: 'flex', gap: '0.75rem' }}>
            {onClose && (
              <button onClick={onClose} style={{ flex: 1, padding: '0.75rem 1.5rem', background: THEME.cardBg, color: THEME.textPrimary, borderRadius: '0.75rem', fontWeight: 600, border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Cancel</button>
            )}
            <button onClick={handleSave} style={{ flex: onClose ? 1 : undefined, width: onClose ? undefined : '100%', padding: '0.75rem 1.5rem', background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})`, color: '#fff', borderRadius: '0.75rem', fontWeight: 600, border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>Save Changes</button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: THEME.panelBg, borderRadius: '0.75rem', padding: '1.5rem', width: '24rem', border: `1px solid ${THEME.border}` }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font }}>New Jersey Template</h3>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="Template name..." maxLength={16} autoFocus
              style={{ width: '100%', padding: '0.75rem 1rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font, outline: 'none' }} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowAddModal(false)} style={{ flex: 1, padding: '0.5rem 1rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Cancel</button>
              <button onClick={handleAddTemplate} style={{ flex: 1, padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})`, color: '#fff', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {showRenameModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: THEME.panelBg, borderRadius: '0.75rem', padding: '1.5rem', width: '24rem', border: `1px solid ${THEME.border}` }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font }}>Rename Template</h3>
            <input type="text" value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="New name..." maxLength={16} autoFocus
              style={{ width: '100%', padding: '0.75rem 1rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font, outline: 'none' }} />
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowRenameModal(false)} style={{ flex: 1, padding: '0.5rem 1rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Cancel</button>
              <button onClick={handleRenameTemplate} style={{ flex: 1, padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${THEME.accent}, ${THEME.accentEnd})`, color: '#fff', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>Rename</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && templateToDelete && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: THEME.panelBg, borderRadius: '0.75rem', padding: '1.5rem', width: '24rem', border: `1px solid ${THEME.border}` }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: '#f87171', marginBottom: '0.5rem', fontFamily: THEME.font }}>Delete Template?</h3>
            <p style={{ color: THEME.textSecondary, marginBottom: '1.5rem', fontFamily: THEME.font }}>Delete "<strong>{templateToDelete.name}</strong>"? This cannot be undone.</p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '0.5rem 1rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Cancel</button>
              <button onClick={handleRemoveTemplate} style={{ flex: 1, padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${THEME.danger}, ${THEME.dangerEnd})`, color: '#fff', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showSavePresetModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ background: THEME.panelBg, borderRadius: '0.75rem', padding: '1.5rem', width: '24rem', border: `1px solid ${THEME.border}` }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: 700, color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font }}>Save as Preset</h3>
            <p style={{ color: THEME.textSecondary, fontSize: '0.875rem', marginBottom: '1rem', fontFamily: THEME.font }}>Save current colors and font as a reusable preset.</p>
            <input type="text" value={newPresetName} onChange={(e) => setNewPresetName(e.target.value)}
              placeholder="Preset name..." maxLength={20} autoFocus
              style={{ width: '100%', padding: '0.75rem 1rem', background: THEME.inputBg, border: `1px solid ${THEME.border}`, borderRadius: '0.5rem', color: THEME.textPrimary, marginBottom: '1rem', fontFamily: THEME.font, outline: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', padding: '0.75rem', background: THEME.cardBg, borderRadius: '0.5rem', border: `1px solid ${THEME.border}` }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '0.5rem', border: '2px solid', backgroundColor: editingTemplate?.fillColor, borderColor: editingTemplate?.outlineColor }} />
              <div style={{ fontSize: '0.875rem' }}>
                <div style={{ color: THEME.textPrimary, fontWeight: 500, fontFamily: THEME.font }}>{editingTemplate?.name}</div>
                <div style={{ color: THEME.textSecondary, fontFamily: THEME.font }}>Current colors will be saved</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => { setShowSavePresetModal(false); setNewPresetName(''); }} style={{ flex: 1, padding: '0.5rem 1rem', background: THEME.cardBg, color: THEME.textSecondary, borderRadius: '0.5rem', border: `1px solid ${THEME.border}`, cursor: 'pointer', fontFamily: THEME.font }}>Cancel</button>
              <button onClick={saveAsPreset} disabled={!newPresetName.trim()} style={{ flex: 1, padding: '0.5rem 1rem', background: `linear-gradient(135deg, ${THEME.success}, #34d399)`, color: '#fff', borderRadius: '0.5rem', border: 'none', cursor: 'pointer', fontFamily: THEME.font, opacity: !newPresetName.trim() ? 0.5 : 1 }}>Save Preset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamSettings;
